import {
  ChangeDetectionStrategy,
  Component,
  computed,
  CUSTOM_ELEMENTS_SCHEMA,
  effect,
  inject,
  input,
  type OnDestroy,
  signal,
  viewChild,
  type ElementRef,
} from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { FormControl, ReactiveFormsModule } from '@angular/forms';
import { TranslatePipe } from '@ngx-translate/core';
import { ExclusiveAudioPlaybackService } from '../../services/exclusive-audio-playback/exclusive-audio-playback';
import { RangeSliderComponent } from '../range-slider/range-slider';

const SECONDS_PER_MINUTE = 60;
const SECONDS_STRING_LENGTH = 2;
const MS_PER_SECOND = 1000;
const DEFAULT_VOLUME = 0.5;
const PERCENT_MAX = 100;
const END_OF_TRACK_EPSILON_SECONDS = 0.3;

let nextInstanceId = 0;

@Component({
  selector: 'nr-audio-player',
  imports: [TranslatePipe, ReactiveFormsModule, RangeSliderComponent],
  templateUrl: './audio-player.html',
  styleUrl: './audio-player.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
  schemas: [CUSTOM_ELEMENTS_SCHEMA],
})
export class AudioPlayerComponent implements OnDestroy {
  private readonly audioRef = viewChild.required<ElementRef<HTMLAudioElement>>('audioEl');
  private readonly exclusiveAudioPlayback = inject(ExclusiveAudioPlaybackService);

  readonly durationMs = input<number | undefined>(undefined);

  readonly src = input.required<string>();

  readonly toggleId = `audio-player-toggle-${nextInstanceId}`;

  readonly muteId = `audio-player-mute-${nextInstanceId++}`;

  readonly isPlaying = signal(false);

  readonly currentTime = signal(0);

  readonly nativeDuration = signal(0);

  readonly volume = signal(DEFAULT_VOLUME);

  readonly isMuted = signal(false);

  readonly duration = computed(() => {
    const native = this.nativeDuration();
    if (Number.isFinite(native) && native > 0) return native;
    const provided = this.durationMs();
    return provided !== undefined ? provided / MS_PER_SECOND : native;
  });

  readonly volumeTooltipLabel = computed(() => `${Math.round(this.volume() * PERCENT_MAX)}%`);

  readonly seekControl = new FormControl(0, { nonNullable: true });

  readonly volumeControl = new FormControl(DEFAULT_VOLUME * PERCENT_MAX, { nonNullable: true });

  constructor() {
    effect(() => {
      const current = this.currentTime();
      if (this.seekControl.value !== current) {
        this.seekControl.setValue(current, { emitEvent: false });
      }
    });

    this.seekControl.valueChanges.pipe(takeUntilDestroyed()).subscribe((value) => {
      this.seek(value);
    });

    this.volumeControl.valueChanges.pipe(takeUntilDestroyed()).subscribe((value) => {
      this.setVolume(value);
    });
  }

  ngOnDestroy(): void {
    this.exclusiveAudioPlayback.release(this.audioRef().nativeElement);
  }

  togglePlay(): void {
    const audio = this.audioRef().nativeElement;
    if (audio.paused) {
      void audio.play();
    } else {
      audio.pause();
    }
  }

  onTimeUpdate(): void {
    this.currentTime.set(this.snapNearEnd(this.audioRef().nativeElement.currentTime));
  }

  onLoadedMetadata(): void {
    this.nativeDuration.set(this.audioRef().nativeElement.duration);
  }

  onPlay(): void {
    this.exclusiveAudioPlayback.claim(this.audioRef().nativeElement);
    this.isPlaying.set(true);
  }

  onPause(): void {
    this.isPlaying.set(false);
  }

  onEnded(): void {
    this.isPlaying.set(false);
    this.currentTime.set(this.duration());
  }

  seek(value: number): void {
    this.audioRef().nativeElement.currentTime = value;
    this.currentTime.set(this.snapNearEnd(value));
  }

  toggleMute(): void {
    const audio = this.audioRef().nativeElement;
    audio.muted = !audio.muted;
    this.isMuted.set(audio.muted);
  }

  setVolume(value: number): void {
    const audio = this.audioRef().nativeElement;
    audio.volume = value / PERCENT_MAX;
    this.volume.set(audio.volume);
    if (value > 0 && audio.muted) {
      audio.muted = false;
      this.isMuted.set(false);
    }
  }

  formattedCurrentTime(): string {
    return AudioPlayerComponent.formatSeconds(this.currentTime());
  }

  formattedDuration(): string {
    return AudioPlayerComponent.formatSeconds(this.duration());
  }

  private snapNearEnd(time: number): number {
    const total = this.duration();
    return total > 0 && total - time < END_OF_TRACK_EPSILON_SECONDS ? total : time;
  }

  private static formatSeconds(totalSeconds: number): string {
    if (!Number.isFinite(totalSeconds)) return '0:00';
    const minutes = Math.floor(totalSeconds / SECONDS_PER_MINUTE);
    const seconds = Math.floor(totalSeconds % SECONDS_PER_MINUTE);
    return `${minutes}:${seconds.toString().padStart(SECONDS_STRING_LENGTH, '0')}`;
  }
}
